import { useMemo } from 'react';

import { useJobQuoteContext } from 'components/JobQuote/context';

import { PLACEHOLDER_ITEMS } from './constants';
import { JobQuoteLineItem, formatCustomItem, formatKandaItem } from './helpers';

export interface JobQuoteBreakdownHook {
  lineItems?: JobQuoteLineItem[];
  isLoading: boolean;
  bodyClassName: string;
  headingClassName: string;
  gridClassName: string;
  compact: boolean;
}

export default function useJobQuoteBreakdown(): JobQuoteBreakdownHook {
  const { job, isLoading, variant } = useJobQuoteContext();

  const compact = useMemo(() => variant !== 'full', [variant]);

  const lineItems = useMemo(() => {
    if (!job || isLoading) return PLACEHOLDER_ITEMS;
    const items = job.job_items;
    return items.map(
      (jobItem) => {
        const { quote_document: doc } = jobItem;
        if (doc) return formatCustomItem(jobItem, doc);
        return formatKandaItem(jobItem);
      },
      [job],
    );
  }, [job, isLoading]);

  const bodyClassName = useMemo(
    () =>
      [
        'flex flex-col px-4 py-3 border-b border-neutral-300',
        compact ? '' : 'md:px-6 md:py-5',
      ].join(' '),
    [compact],
  );

  const headingClassName = useMemo(
    () =>
      ['text-16-24-em  mb-2', compact ? '' : 'md:text-18-22-em md:mb-4'].join(
        ' ',
      ),
    [compact],
  );

  const gridClassName = useMemo(
    () =>
      [
        'grid grid-cols-[auto_min-content] gap-x-3 gap-y-2 max-w-full',
        compact ? '' : 'md:gap-x-5 md:gap-y-3 ',
      ].join(' '),
    [compact],
  );

  return {
    lineItems,
    isLoading,
    bodyClassName,
    headingClassName,
    gridClassName,
    compact,
  };
}
